// src/client/pages/dbadmin.tsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiClient } from '@client/lib/api-client';
import { queryKeys } from '@client/lib/query-keys';
import type {
  DbAdminPageProps,
  TableInfo,
  TableDetails,
  AdminQueryResponse,
  ClearTableResponse,
  PopulateDataResponse
} from '@client/types';
import type { SchemaDiffResponse, MigrationResult } from '@shared/types/admin/migration.types';
import { Database, Search, Trash2, Download, AlertTriangle, CheckCircle } from 'lucide-react';
import SchemaMigrationSection from '@client/components/admin/SchemaMigrationSection';

// ============================================================================
// Local Types
// ============================================================================

interface SearchQuery {
  tableName: string;
  searchTerm: string;
  textColumns: string[];
}

// ============================================================================
// Table List Component
// ============================================================================

interface TableListProps {
  tables: TableInfo[];
  selectedTable: string | null;
  onSelectTable: (tableName: string) => void;
}

function TableList({ tables, selectedTable, onSelectTable }: TableListProps) {
  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="px-4 py-3 border-b bg-gray-50 rounded-t-lg">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <Database className="h-4 w-4" />
          Tabellen ({tables.length})
        </h2>
      </div>
      <div className="p-4 max-h-96 overflow-y-auto">
        {tables.map((table) => (
          <button
            key={table.name}
            onClick={() => onSelectTable(table.name)}
            className={`w-full text-left px-3 py-2 rounded transition-colors ${
              selectedTable === table.name
                ? 'bg-blue-100 text-blue-800 border border-blue-200'
                : 'hover:bg-gray-100 text-gray-700'
            }`}
          >
            <div className="font-medium">{table.name}</div>
            <div className="text-xs text-gray-500 mt-1">{table.type}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Table Header Component
// ============================================================================

interface TableHeaderProps {
  tableName: string;
  rowCount: number;
  columnCount: number;
  isLoading: boolean;
  onPopulateData: () => void;
  onClearTable: () => void;
  onExportJson: () => void;
  isPopulating: boolean;
  isClearing: boolean;
  isExporting: boolean;
}

function TableHeader({
  tableName,
  rowCount,
  columnCount,
  isLoading,
  onPopulateData,
  onClearTable,
  onExportJson,
  isPopulating,
  isClearing,
  isExporting
}: TableHeaderProps) {
  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="px-4 py-3 border-b bg-gray-50 rounded-t-lg flex justify-between items-center">
        <h2 className="font-semibold text-gray-900">Tabelle: {tableName}</h2>
        <div className="flex space-x-2">
          <button
            onClick={onExportJson}
            disabled={isExporting}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                Exportiere...
              </>
            ) : (
              <>
                <Download className="h-3 w-3" />
                JSON Export
              </>
            )}
          </button>
          <button
            onClick={onPopulateData}
            disabled={isPopulating}
            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
          >
            {isPopulating ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                Lade...
              </>
            ) : (
              <>
                <Download className="h-3 w-3" />
                Default Daten laden
              </>
            )}
          </button>
          <button
            onClick={onClearTable}
            disabled={isClearing}
            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
          >
            {isClearing ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                Lösche...
              </>
            ) : (
              <>
                <Trash2 className="h-3 w-3" />
                Alle Daten löschen
              </>
            )}
          </button>
        </div>
      </div>
      {isLoading ? (
        <div className="p-4 text-center text-gray-500">Lade Details...</div>
      ) : (
        <div className="p-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Anzahl Zeilen:</span> {rowCount.toLocaleString()}
            </div>
            <div>
              <span className="font-medium">Spalten:</span> {columnCount}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Schema View Component
// ============================================================================

interface SchemaViewProps {
  tableDetails: TableDetails;
}

function SchemaView({ tableDetails }: SchemaViewProps) {
  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="px-4 py-3 border-b bg-gray-50">
        <h3 className="font-semibold text-gray-900">Schema</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-900">Spalte</th>
              <th className="px-4 py-2 text-left font-medium text-gray-900">Typ</th>
              <th className="px-4 py-2 text-left font-medium text-gray-900">Nullable</th>
              <th className="px-4 py-2 text-left font-medium text-gray-900">Primary Key</th>
              <th className="px-4 py-2 text-left font-medium text-gray-900">Default</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {tableDetails.schema.map((col) => (
              <tr key={col.cid} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium">{col.name}</td>
                <td className="px-4 py-2 text-gray-600">{col.type}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    col.notnull ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {col.notnull ? 'NOT NULL' : 'NULL'}
                  </span>
                </td>
                <td className="px-4 py-2">
                  {col.pk && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                      PK
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-gray-600">
                  {col.dflt_value !== null ? String(col.dflt_value) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Search Component
// ============================================================================

interface SearchComponentProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  isLoading: boolean;
}

function SearchComponent({ searchTerm, onSearchChange, isLoading }: SearchComponentProps) {
  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="px-4 py-3 border-b bg-gray-50">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Search className="h-4 w-4" />
          Suche
        </h3>
      </div>
      <div className="p-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Suche in allen Textfeldern..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        </div>
        {isLoading && searchTerm && (
          <div className="mt-2 text-sm text-gray-500 flex items-center gap-2">
            <div className="animate-spin rounded-full h-3 w-3 border-2 border-gray-500 border-t-transparent" />
            Suche...
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Data Table Component
// ============================================================================

interface DataTableProps {
  data: any[];
  title: string;
  isLoading: boolean;
}

function DataTable({ data, title, isLoading }: DataTableProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow border">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="p-4 text-center text-gray-500">Lade Daten...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow border">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="p-8 text-center text-gray-500">
          <Database className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>Keine Daten vorhanden</p>
        </div>
      </div>
    );
  }

  const columns = Object.keys(data[0] || {});

  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="px-4 py-3 border-b bg-gray-50">
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => (
                <th key={col} className="px-4 py-2 text-left font-medium text-gray-900">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((row, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                {columns.map((col) => (
                  <td key={col} className="px-4 py-2 text-gray-600 max-w-xs truncate">
                    {row[col] !== null && row[col] !== undefined ? String(row[col]) : '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Confirmation Modal Component
// ============================================================================

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  tableName: string;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  deletedRows?: number;
  errorMessage?: string;
}

function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  tableName,
  isLoading,
  isSuccess,
  isError,
  deletedRows,
  errorMessage
}: ConfirmationModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            Alle Daten löschen
          </h3>
        </div>
        <div className="px-6 py-4">
          <p className="text-gray-600 mb-4">
            Sind Sie sicher, dass Sie <strong>alle Daten</strong> aus der Tabelle{' '}
            <strong>{tableName}</strong> löschen möchten?
          </p>
          <p className="text-red-600 text-sm font-medium">
            ⚠️ Diese Aktion kann nicht rückgängig gemacht werden!
          </p>
          {isSuccess && (
            <div className="mt-3 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              {deletedRows || 0} Zeilen erfolgreich gelöscht
            </div>
          )}
          {isError && (
            <div className="mt-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Fehler: {errorMessage}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Lösche...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                Alle Daten löschen
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main DbAdmin Component
// ============================================================================

export default function DbAdmin(_props: DbAdminPageProps) {
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const queryClient = useQueryClient();

  // ============================================================================
  // Queries
  // ============================================================================

  const { data: tables = [], isLoading: tablesLoading, error: tablesError } = useQuery<TableInfo[], Error>({
    queryKey: queryKeys.admin.tables(),
    queryFn: () => ApiClient.get<TableInfo[]>('/admin/schema/tables'),
  });

  const { data: tableDetails, isLoading: detailsLoading } = useQuery<TableDetails, Error>({
    queryKey: queryKeys.admin.tableDetails(selectedTable!),
    queryFn: () => ApiClient.get<TableDetails>(`/admin/schema/tables/${selectedTable}`),
    enabled: !!selectedTable,
  });

  const { data: sampleData, isLoading: sampleLoading } = useQuery<AdminQueryResponse, Error>({
    queryKey: queryKeys.admin.sampleData(selectedTable!, 5),
    queryFn: () => ApiClient.post<AdminQueryResponse>('/admin/query', {
      query: `SELECT * FROM ${selectedTable} LIMIT 5`
    }),
    enabled: !!selectedTable,
  });

  const { data: searchResults, isLoading: searchLoading } = useQuery<AdminQueryResponse, Error>({
    queryKey: queryKeys.admin.search(selectedTable!, searchTerm),
    queryFn: () => {
      if (!tableDetails?.schema || !searchTerm.trim()) {
        return { success: true, data: [] };
      }

      const textColumns = tableDetails.schema
        .filter(col => col.type.toLowerCase().includes('text') || col.type.toLowerCase().includes('varchar'))
        .map(col => col.name);

      if (textColumns.length === 0) {
        return { success: true, data: [] };
      }

      const whereClause = textColumns
        .map(col => `${col} LIKE '%${searchTerm}%'`)
        .join(' OR ');

      return ApiClient.post<AdminQueryResponse>('/admin/query', {
        query: `SELECT * FROM ${selectedTable} WHERE ${whereClause} LIMIT 20`
      });
    },
    enabled: !!selectedTable && !!tableDetails && searchTerm.trim().length > 0,
  });

  // ============================================================================
  // Mutations
  // ============================================================================

  const clearTableMutation = useMutation({
    mutationFn: (tableName: string) =>
      ApiClient.delete<ClearTableResponse>(`/admin/tables/${tableName}/clear`),
    onSuccess: (_data, tableName) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.tableDetails(tableName) });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.sampleData(tableName, 5) });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.search(tableName, searchTerm) });
      setSearchTerm('');
    },
  });

  const populateDataMutation = useMutation({
    mutationFn: (tableName: string) =>
      ApiClient.post<PopulateDataResponse>('/admin/populate', { tableName }),
    onSuccess: (_data, tableName) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.tableDetails(tableName) });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.sampleData(tableName, 5) });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.search(tableName, searchTerm) });
    },
  });

  const exportJsonMutation = useMutation({
    mutationFn: async (tableName: string) => {
      const response = await ApiClient.get<any>(
        `/admin/tables/${tableName}/export?includeMetadata=true`
      );
      
      const jsonString = JSON.stringify(response, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tableName}_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      return { success: true };
    },
  });

  // Schema Migration Mutations
  const checkSchemaMutation = useMutation({
    mutationFn: () => ApiClient.get<SchemaDiffResponse>('/admin/schema/diff'),
  });

  const executeMigrationMutation = useMutation({
    mutationFn: () => ApiClient.post<MigrationResult>('/admin/schema/migrate'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.tables() });
      checkSchemaMutation.mutate(); // Refresh diff after migration
    },
  });

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleSelectTable = (tableName: string) => {
    setSelectedTable(tableName);
    setSearchTerm('');
  };

  const handleClearTable = () => {
    if (selectedTable) {
      clearTableMutation.mutate(selectedTable);
    }
  };

  const handlePopulateData = () => {
    if (selectedTable) {
      populateDataMutation.mutate(selectedTable);
    }
  };

  const handleExportJson = () => {
    if (selectedTable) {
      exportJsonMutation.mutate(selectedTable);
    }
  };

  const handleCheckSchema = () => {
    checkSchemaMutation.mutate();
  };

  const handleExecuteMigration = () => {
    executeMigrationMutation.mutate();
  };

  // ============================================================================
  // Render States
  // ============================================================================

  if (tablesLoading) {
    return (
      <div className="px-6 pb-6 space-y-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Database className="h-6 w-6" />
          DB Admin
        </h1>
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">Lade Tabellen...</div>
        </div>
      </div>
    );
  }

  if (tablesError) {
    return (
      <div className="px-6 pb-6 space-y-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Database className="h-6 w-6" />
          DB Admin
        </h1>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Fehler beim Laden der Tabellen: {tablesError.message}
        </div>
      </div>
    );
  }

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <div className="px-6 pb-6 space-y-6">
      <h1 className="text-2xl font-bold flex items-center gap-2">
        <Database className="h-6 w-6" />
        DB Admin
      </h1>

      {/* Success/Error Messages */}
      {exportJsonMutation.isSuccess && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          JSON-Export erfolgreich heruntergeladen
        </div>
      )}
      {exportJsonMutation.isError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Fehler beim JSON-Export: {exportJsonMutation.error?.message}
        </div>
      )}
      {populateDataMutation.isSuccess && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          Default-Daten erfolgreich geladen
        </div>
      )}
      {populateDataMutation.isError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Fehler beim Laden der Default-Daten: {populateDataMutation.error?.message}
        </div>
      )}
      {checkSchemaMutation.isError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Fehler beim Schema-Check: {checkSchemaMutation.error?.message}
        </div>
      )}

      {/* Schema Migration Section */}
      <SchemaMigrationSection
        onCheckSchema={handleCheckSchema}
        onExecuteMigration={handleExecuteMigration}
        isChecking={checkSchemaMutation.isPending}
        isExecuting={executeMigrationMutation.isPending}
        diffData={checkSchemaMutation.data || null}
        migrationResult={executeMigrationMutation.data || null}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tables List */}
        <div className="lg:col-span-1">
          <TableList
            tables={tables}
            selectedTable={selectedTable}
            onSelectTable={handleSelectTable}
          />
        </div>

        {/* Table Details */}
        <div className="lg:col-span-2">
          {!selectedTable ? (
            <div className="bg-white rounded-lg shadow border p-8 text-center">
              <Database className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <div className="text-gray-500">Wählen Sie eine Tabelle aus der Liste</div>
            </div>
          ) : (
            <div className="space-y-6">
              <TableHeader
                tableName={selectedTable}
                rowCount={tableDetails?.stats?.rowCount || 0}
                columnCount={tableDetails?.schema?.length || 0}
                isLoading={detailsLoading}
                onPopulateData={handlePopulateData}
                onClearTable={() => setShowClearConfirm(true)}
                onExportJson={handleExportJson}
                isPopulating={populateDataMutation.isPending}
                isClearing={clearTableMutation.isPending}
                isExporting={exportJsonMutation.isPending}
              />

              {tableDetails && <SchemaView tableDetails={tableDetails} />}

              {tableDetails && (
                <SearchComponent
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  isLoading={searchLoading}
                />
              )}

              {tableDetails && (
                <DataTable
                  data={searchTerm ? (searchResults?.data || []) : (sampleData?.data || [])}
                  title={searchTerm ? `Suchergebnisse (${searchResults?.data?.length || 0})` : 'Beispieldaten (erste 5 Zeilen)'}
                  isLoading={searchTerm ? searchLoading : sampleLoading}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Clear Table Confirmation Modal */}
      <ConfirmationModal
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={handleClearTable}
        tableName={selectedTable || ''}
        isLoading={clearTableMutation.isPending}
        isSuccess={clearTableMutation.isSuccess}
        isError={clearTableMutation.isError}
        deletedRows={clearTableMutation.data?.deletedRows}
        errorMessage={clearTableMutation.error?.message}
      />
    </div>
  );
}